import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { initialObservationRatingDetails } from './observationSliceConstants'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { observationApi } from '../../../middleware/api/Performance/Observation/observationApi'
import { AppDispatch, RootState } from '../../../stateStore'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import {
  GetObservationsApiProps,
  Observation,
  ObservationActiveEmployee,
  ObservationPayload,
  ObservationSliceState,
  PerformanceRating,
} from '../../../types/Performance/Observation/observationTypes'
import { PageDetails } from '../../../types/Performance/MyReview/myReviewTypes'

const initialObservationState: ObservationSliceState = {
  isLoading: ApiLoadingState.idle,
  error: null,
  ratingDetails: initialObservationRatingDetails,
  activeEmployees: [],
  performanceRatings: [],
  observations: [],
  totalSize: 0,
  downloadObservationFile: null,
}

const addObservation = createAsyncThunk<
  number,
  ObservationPayload,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'observation/addObservation',
  async (payload: ObservationPayload, thunkApi) => {
    try {
      return await observationApi.addObservation(payload)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const uploadCandidateObservation = createAsyncThunk<
  void,
  { personId: number; file: File },
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'observation/uploadCandidateObservation',
  async ({ personId, file }, thunkApi) => {
    try {
      return await observationApi.uploadCandidateObservation(personId, file)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getObservationRatingDetails = createAsyncThunk<
  PageDetails,
  void,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('observation/getObservationRatingDetails', async (_, thunkApi) => {
  try {
    return await observationApi.getObservationRatingDetails()
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const getActiveEmployees = createAsyncThunk<
  ObservationActiveEmployee[],
  void,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('observation/getActiveEmployees', async (_, thunkApi) => {
  try {
    const result = await observationApi.getActiveEmployees()
    return result as ObservationActiveEmployee[]
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const getPerformanceRatings = createAsyncThunk<
  PerformanceRating[],
  void,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('observation/getPerformanceRatings', async (_, thunkApi) => {
  try {
    return await observationApi.getPerformanceRatings()
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})
const deleteObservation = createAsyncThunk<
  number,
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('observation/deleteObservation', async (observationId, thunkApi) => {
  try {
    await observationApi.deleteObservation(observationId)
    return observationId
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})
const updateObservation = createAsyncThunk(
  'observation/updateObservation',
  async (data: Observation, thunkApi) => {
    try {
      return await observationApi.updateObservation(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const getAllObservations = createAsyncThunk(
  'observation/getAllObservations',
  async (props: GetObservationsApiProps, thunkApi) => {
    try {
      return await observationApi.getAllObservations(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const downloadObservationFile = createAsyncThunk(
  'observation/downloadObservationFile',
  async (fileName: string, thunkApi) => {
    try {
      return await observationApi.downloadObservationFile(fileName)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const exportObservation = createAsyncThunk(
  'observation/exportObservation',
  async (
    props: {
      searchInput: string
      observationType: string
    },
    thunkApi,
  ) => {
    try {
      return await observationApi.exportObservation(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const observationSlice = createSlice({
  name: 'observation',
  initialState: initialObservationState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(addObservation.fulfilled, (state) => {
        state.isLoading = ApiLoadingState.succeeded
      })
      .addCase(uploadCandidateObservation.fulfilled, (state) => {
        state.isLoading = ApiLoadingState.succeeded
      })
      .addCase(getObservationRatingDetails.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.ratingDetails = action.payload
      })
      .addCase(getActiveEmployees.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.activeEmployees = action.payload
      })
      .addCase(getPerformanceRatings.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.performanceRatings = action.payload
      })
      .addCase(getAllObservations.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.observations = action.payload.list
        state.totalSize = action.payload.size
      })
      .addCase(updateObservation.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        const index = state.observations.findIndex(
          (obs) => obs.id === action.payload.id,
        )
        if (index !== -1) {
          state.observations[index] = action.payload
        }
      })
      .addCase(deleteObservation.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.observations = state.observations.filter(
          (obs) => obs.id !== action.payload,
        )
      })
      .addCase(exportObservation.fulfilled, (state) => {
        state.isLoading = ApiLoadingState.succeeded
      })
      .addMatcher(
        isAnyOf(
          addObservation.pending,
          uploadCandidateObservation.pending,
          getObservationRatingDetails.pending,
          getActiveEmployees.pending,
          getPerformanceRatings.pending,
          getAllObservations.pending,
          updateObservation.pending,
          deleteObservation.pending,
          exportObservation.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
      .addMatcher(
        isAnyOf(
          addObservation.rejected,
          uploadCandidateObservation.rejected,
          getObservationRatingDetails.rejected,
          getActiveEmployees.rejected,
          getPerformanceRatings.rejected,
          getAllObservations.rejected,
          updateObservation.rejected,
          deleteObservation.rejected,
          exportObservation.rejected,
        ),
        (state, action) => {
          state.isLoading = ApiLoadingState.failed
          state.error = action.payload as ValidationError
        },
      )
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.observation.isLoading

const ratingDetails = (state: RootState): PageDetails =>
  state.observation.ratingDetails

const activeEmployees = (state: RootState): ObservationActiveEmployee[] =>
  state.observation.activeEmployees

const performanceRatings = (state: RootState): PerformanceRating[] =>
  state.observation.performanceRatings

const observations = (state: RootState): Observation[] =>
  state.observation.observations

const totalSize = (state: RootState): number => state.observation.totalSize

const observationThunk = {
  addObservation,
  uploadCandidateObservation,
  getObservationRatingDetails,
  getActiveEmployees,
  getPerformanceRatings,
  getAllObservations,
  updateObservation,
  deleteObservation,
  downloadObservationFile,
}

const observationSelectors = {
  isLoading,
  ratingDetails,
  activeEmployees,
  performanceRatings,
  observations,
  totalSize,
}

export const observationService = {
  thunk: observationThunk,
  actions: observationSlice.actions,
  reducer: observationSlice.reducer,
  selectors: observationSelectors,
}

export default observationSlice.reducer
